import { SEARCH_FLAGS } from '@/constants/experimentConstants';
import useViewToggle from '@/features/searchV2/components/ViewToggle/ViewToggle';
import { SearchContext } from '@/features/searchV2/types/tracking';
import { locationSlug } from '@/helpers/urlTs';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { SearchManagerOutput } from '@/hooks/search/useSearch';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
  SEARCH_PAGE_VIEWS,
} from '@/hooks/search/useSearch.constants';
import {
  buildServerRequestSearchParams,
  buildUrlSearchParams,
  getMatchingKeyword,
  getMatchingLocation,
  getSearchUrlParameters,
  resolvePerformSearchParams,
  withNearMeDefaultSort,
} from '@/hooks/search/useSearch.helpers';
import { PerformSearchOptions } from '@/hooks/search/useSearch.types';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import { SEARCH_ACTION } from '@/reducers/searchV2Reducer';
import { companiesAction, SerpTabCounts } from '@/reducers/unifiedSearchReducer';
import { placeService } from '@/services';
import { Tracking } from '@/types/analytics';
import { searchFeatureFlags as SearchFeatureFlags, UrlSearchState } from '@/types/state/search';
import debounce from 'lodash.debounce';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { SerpTabId } from '../SerpTabs.types';
import { buildSerpTabHref, getSerpSearchIdentity, getSerpTabPath, isIndexableSearch } from '../unifiedSearch.urls';
import {
  findSearchCounts,
  getCompaniesCountKey,
  getCompaniesRequestKey,
  getCountsKey,
  requestCompanies,
  requestCompaniesExtended,
  trackCompaniesSearchPerformed,
} from './companiesSearch.helpers';

const DEBOUNCE_SEARCH_REQUEST_MS = 500;

type LocationState = { searchContext?: SearchContext; keywordSubmitted?: boolean; serpTabSwitch?: boolean };

type SearchTrigger = {
  searchContext?: SearchContext;
  keywordSubmitted?: boolean;
  immediate?: boolean;
  force?: boolean;
};

type CompaniesPerformSearchOptions = PerformSearchOptions & { extendedSearch?: boolean };

export type CompaniesSearchManager = SearchManagerOutput & {
  profilePages: SearchManagerOutput['places'][number];
  /** Whether the list in the URL is loaded, still loading, or failed (no empty state after a failure). */
  status: 'loading' | 'loaded' | 'failed';
  /** Which tab counts are still for an earlier search or filter, so their badges show as loading. */
  pendingCounts: Record<SerpTabId, boolean>;
  /** The tab counts held for the search in the URL, empty until something has counted it. */
  counts: SerpTabCounts;
};

const DATE_PARAMS = ['startDate', 'endDate', 'timeOfDay'] as const;

const useSearchFeatureFlags = (): SearchFeatureFlags => {
  const flags = useAppSelector((state) => state.flags);
  const getVariant = useGetAmplitudeExperimentVariant();
  const searchFlags: SearchFeatureFlags = {};
  SEARCH_FLAGS.forEach((flag) => {
    const value = flags[flag]?.value ?? getVariant(flag)?.value ?? undefined;
    if (value) {
      searchFlags[flag] = value.toString();
    }
  });
  return searchFlags;
};

/**
 * The Companies tab: today's regular search behaviour on the tabbed page's own store. The URL is the
 * source of truth while the tab is active; actions push URLs and one effect decides between fetching,
 * fetching a further page and showing what is already in memory.
 */
export function useCompaniesSearch({ isActive }: { isActive: boolean }): CompaniesSearchManager {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const match = useRouteMatch<{ q?: string; location?: string }>();
  const unifiedSearch = useAppSelector((state) => state.unifiedSearch);
  const { companies, companiesRequestKey, companiesFailedRequestKey, tabQueries } = unifiedSearch;
  const searchFlags = useSearchFeatureFlags();

  const urlState = getSearchUrlParameters({ match });
  const page = urlState.page ?? 0;

  const viewToggle = useViewToggle({
    views: [...SEARCH_PAGE_VIEWS],
    initialState: SEARCH_PAGE_VIEWS[0],
    className: 'px-sm',
  });
  const screenName: Tracking.ScreenName = viewToggle.view === 'map' ? 'search_results_map' : 'search_results';

  const [syncNonce, setSyncNonce] = useState(0);
  const triggerRef = useRef<SearchTrigger | undefined>(undefined);
  const wasActiveRef = useRef<boolean | undefined>(undefined);
  const requestIdRef = useRef(0);
  const inFlightRef = useRef<{ id: number; requestKey: string; page: number } | undefined>(undefined);
  const latestRef = useRef({ unifiedSearch, urlState, screenName, searchFlags, isActive });
  latestRef.current = { unifiedSearch, urlState, screenName, searchFlags, isActive };

  const debouncedRun = useRef(debounce((run: () => void) => run(), DEBOUNCE_SEARCH_REQUEST_MS)).current;
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      debouncedRun.cancel();
      isMountedRef.current = false;
    },
    [debouncedRun],
  );

  useEffect(() => {
    if (!isActive) {
      debouncedRun.cancel();
      wasActiveRef.current = false;
      return;
    }

    const trigger = triggerRef.current;
    triggerRef.current = undefined;
    const isFirstRun = wasActiveRef.current === undefined;
    const switchedIn = wasActiveRef.current === false;
    wasActiveRef.current = true;
    const isTabSwitch = switchedIn && (history.action === 'POP' || location.state?.serpTabSwitch === true);
    const arrivalContext: SearchContext = isTabSwitch ? 'tab' : location.state?.searchContext ?? 'navigation';

    const {
      unifiedSearch: state,
      urlState: params,
      screenName: currentScreenName,
      searchFlags: flags,
    } = latestRef.current;
    const currentPage = params.page ?? 0;
    const requestKey = getCompaniesRequestKey(params, state.companies.bounds);
    const isNewSearch = requestKey !== state.companiesRequestKey;
    const countsKey = getCountsKey(params, state.companies.bounds);
    const heldCounts = state.countsKey === countsKey ? state.countsBySearch[countsKey] : undefined;
    const counts = heldCounts?.sistaminuten === undefined ? undefined : heldCounts;
    const requestParams = getCompaniesRequestParams(params, state.companies, flags);

    if (!trigger?.force && !isNewSearch && state.companies.places[currentPage] !== undefined) {
      debouncedRun.cancel();
      requestIdRef.current++;
      if (state.companies.fetching) {
        dispatch(companiesAction({ type: SEARCH_ACTION.SET_FETCHING, payload: false }));
      }
      if (switchedIn && !isTabSwitch) {
        trackCompaniesSearchPerformed({
          urlState: requestParams,
          searchState: state.companies,
          context: arrivalContext,
          screenName: currentScreenName,
          counts: state.countsBySearch[countsKey] ?? {},
        });
      }
      return;
    }

    const inFlight = inFlightRef.current;
    if (
      !trigger?.force &&
      inFlight?.id === requestIdRef.current &&
      inFlight.requestKey === requestKey &&
      inFlight.page === currentPage
    ) {
      debouncedRun.cancel();
      return;
    }

    const searchContext: SearchContext | undefined = trigger
      ? trigger.searchContext
      : switchedIn || isFirstRun
      ? arrivalContext
      : location.state?.searchContext;
    const keywordSubmitted = trigger
      ? trigger.keywordSubmitted
      : history.action !== 'POP'
      ? location.state?.keywordSubmitted
      : undefined;

    placeService.removeStoredPlaceImpressions();
    dispatch(companiesAction({ type: SEARCH_ACTION.SET_FETCHING, payload: true }));

    const run = () => {
      const requestId = ++requestIdRef.current;
      inFlightRef.current = { id: requestId, requestKey, page: currentPage };
      requestCompanies(
        {
          params: requestParams,
          state: latestRef.current.unifiedSearch.companies,
          requestKey,
          resetAllSearchItems: isNewSearch || Boolean(trigger?.force),
          keywordSubmitted,
          searchContext,
          screenName: currentScreenName,
          countsKey,
          counts,
          isCurrentRequest: () => requestId === requestIdRef.current && latestRef.current.isActive,
        },
        (action) => {
          if (isMountedRef.current) dispatch(action);
        },
      ).finally(() => {
        if (inFlightRef.current?.id === requestId) inFlightRef.current = undefined;
      });
    };

    if (trigger?.immediate) {
      debouncedRun.cancel();
      run();
    } else {
      debouncedRun(run);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, location.key, syncNonce]);

  function navigate(pathname: string, search: string, trigger: SearchTrigger) {
    triggerRef.current = trigger;
    if (`${pathname}${search ? `?${search}` : ''}` !== `${location.pathname}${location.search}`) {
      const { keywordSubmitted, ...state } = location.state ?? {};
      history.push({ pathname, search, state });
    } else {
      setSyncNonce((nonce) => nonce + 1);
    }
  }

  function setBounds(bounds: string | null | undefined) {
    if (bounds === undefined) return;
    dispatch(companiesAction({ type: SEARCH_ACTION.SET_BOUNDS, payload: bounds ?? undefined }));
  }

  /** A new search made while another tab is active lands on the Companies tab with its remembered controls. */
  function performSearchFromOtherTab(params: UrlSearchState, options: CompaniesPerformSearchOptions) {
    const q = 'q' in params ? params.q : urlState.q;
    const searchLocation = 'location' in params ? params.location : urlState.location;
    const lat = 'lat' in params ? params.lat : urlState.lat;
    const lon = 'lon' in params ? params.lon : urlState.lon;
    const search = { q, location: searchLocation, lat, lon };

    const query = new URLSearchParams(tabQueries.places?.query);
    query.delete('page');
    DATE_PARAMS.forEach((key) => {
      if (!(key in params)) return;
      if (params[key]) query.set(key, `${params[key]}`);
      else query.delete(key);
    });

    setBounds(options.bounds);
    triggerRef.current = { searchContext: options.searchContext, keywordSubmitted: options.keywordSubmitted };
    history.push(
      buildSerpTabHref('places', search, { search: getSerpSearchIdentity(search), query: query.toString() }),
    );
  }

  function performSearch(params: UrlSearchState, options: CompaniesPerformSearchOptions = {}) {
    if (!isActive) {
      performSearchFromOtherTab(params, options);
      return;
    }

    if (options.extendedSearch) {
      const requestId = requestIdRef.current;
      const countsKey = getCountsKey(urlState, companies.bounds);
      requestCompaniesExtended(
        {
          params: getCompaniesRequestParams(urlState, companies, searchFlags),
          state: companies,
          countsKey,
          counts: unifiedSearch.countsBySearch[countsKey],
          isCurrentRequest: () => requestId === requestIdRef.current,
        },
        (action) => {
          if (isMountedRef.current) dispatch(action);
        },
      );
      return;
    }

    const resolved = resolvePerformSearchParams(params, urlState, companies, options);
    const nextParams: UrlSearchState = { ...params };

    if (resolved.resetSearch) {
      nextParams.page = null;
    }

    if (
      resolved.updatedLocation &&
      locationSlug(resolved.location) !== DEFAULT_MY_LOCATION_KEYWORD &&
      resolved.sort !== 'closest'
    ) {
      nextParams.lat = null;
      nextParams.lon = null;
    }

    const isIndexable = isIndexableSearch(resolved.q, resolved.location);
    const search = buildUrlSearchParams(nextParams, urlState, isIndexable).toString();
    const pathname = getSerpTabPath('places', { q: resolved.q, location: resolved.location }, isIndexable);

    setBounds(options.bounds);
    navigate(pathname, search, {
      searchContext: options.searchContext,
      keywordSubmitted: options.keywordSubmitted,
      immediate: options.bounds !== undefined,
    });
  }

  function performSyncedSearch() {
    navigate(location.pathname, location.search.replace(/^\?/, ''), {
      searchContext: location.state?.searchContext,
      force: true,
    });
  }

  function paginate() {
    return {
      nextPage: () => {
        performSearch({ page: page + 1 });
      },
      prevPage: () => {
        performSearch({ page: page > 1 ? page - 1 : null });
      },
    };
  }

  const currentRequestKey = getCompaniesRequestKey(urlState, companies.bounds);
  const status = companies.fetching
    ? 'loading'
    : companiesRequestKey === currentRequestKey && companies.places[page] !== undefined
    ? 'loaded'
    : companiesFailedRequestKey === currentRequestKey
    ? 'failed'
    : 'loading';
  const isLoading = isActive && status === 'loading';
  const pendingCounts = {
    places: isLoading && getCompaniesCountKey(companiesRequestKey) !== getCompaniesCountKey(currentRequestKey),
    sistaminuten: isLoading && unifiedSearch.countsKey !== getCountsKey(urlState, companies.bounds),
  };
  const counts =
    findSearchCounts(unifiedSearch.countsBySearch, getCountsKey(urlState, companies.bounds), { anyDates: !isActive }) ??
    {};

  return {
    status,
    pendingCounts,
    counts,
    q: urlState.q?.toLowerCase() !== DEFAULT_SEARCH_KEYWORD ? getMatchingKeyword(urlState.q) : '',
    location:
      urlState.location?.toLowerCase() !== DEFAULT_SEARCH_LOCATION && urlState.location?.toLowerCase() !== 'var'
        ? getMatchingLocation(urlState.location)
        : '',
    startDate: urlState.startDate,
    endDate: urlState.endDate,
    timeOfDay: urlState.timeOfDay,
    prefs: urlState.prefs,
    sort: urlState.sort,
    lat: urlState.lat,
    lon: urlState.lon,
    page,
    places: companies.places || [],
    profilePages: companies.profilePages || [],
    topSearch: companies.topSearch ?? { places: [] },
    results: companies.results || 0,
    fetching: isActive && Boolean(companies.fetching),
    searchContext: companies.searchContext,
    type: 'default',
    performSearch,
    performSyncedSearch,
    paginate,
    viewToggle,
    debugLocation: companies.debugLocation || undefined,
    isIndexable: companies.isIndexable,
  };
}

function getCompaniesRequestParams(
  urlState: UrlSearchState,
  companies: Parameters<typeof buildServerRequestSearchParams>[2],
  searchFlags: SearchFeatureFlags,
): UrlSearchState {
  const params = withNearMeDefaultSort('default', urlState);
  const withFlags = Object.keys(searchFlags).length ? { ...params, searchFeatureFlags: searchFlags } : params;
  // Stringified server params, typed as the regular search types them on the same path.
  return {
    ...buildServerRequestSearchParams(withFlags, urlState, companies),
    q: urlState.q,
    location: urlState.location,
  } as unknown as UrlSearchState;
}
